import React from 'react';
import PropTypes from 'prop-types';
import { Map, List } from 'immutable';
import { compose, withProps, mapProps } from 'recompose';
import round from 'lodash/round';
import moment from 'moment';

import * as timeplanUnitStyles from '../timeplanUnitStyles.css';
import TimeplanUnit from './TimeplanUnit';
import { getDate } from '../../../../lib/dates';
import SummarizedDay from '../SummarizedDay';
// import withResource from '../../../../containers/withResource';
import CustomerName from '../../../../components/CustomerName';
import AddTpu from './AddTpu';
import styles from '../timeplan.css';
import expandedTimeplanUnits from '../../../../entities/TimeplanUnit/expandedTimeplanUnits';

// const Resource = withResource(({ resource: { name } }) => <span>{name}</span>);

const TotalTime = mapProps(({ timeplanUnits }) => ({
  totalHours: round(expandedTimeplanUnits(timeplanUnits.toJS()).reduce((acc, tpu) => {
    const startTime = moment(`${tpu.date} ${tpu.from}`);
    const endTime = moment(`${tpu.date} ${tpu.to}`);
    const duration = moment.duration(endTime.diff(startTime));
    const minutes = duration.asMinutes();
    return acc + minutes;
  }, 0) / 60, 2),
}))(({ totalHours }) => (<span>{totalHours}h</span>));

function TimeplanDay({
  groupedTimeplanUnits,
  groupedCompareTimeplanUnits,
  timeplanUnits,
  disabled,
  date,
  resources,
  workTypes,
  absentReasons,
  onChangeTimeplanUnits,
  isEditable,
  duplicates,
  overlaps,
  bookedTimeplanUnits,
  resourceReportedTimeplanUnits,
  reportedTimeplanUnits,
  allTimeplanUnits,
}) {
  if (!isEditable) {
    return (
      <div className={timeplanUnitStyles.timeplanDay}>
        {groupedTimeplanUnits.valueSeq().map((tpus) => {
          const customerId = tpus.first().get('customer');
          return (
            <div key={customerId} className={styles.timeplanDaySummary}>
              <div>
                <span className={styles.customerName}>
                  <CustomerName noLink customerId={customerId} />
                </span>
                <TotalTime timeplanUnits={tpus} />
              </div>
              <SummarizedDay
                timeplanUnits={tpus}
                compareTimeplanUnits={groupedCompareTimeplanUnits.get(customerId) || new List()}
                overlaps={overlaps}
                duplicates={duplicates}
                absentReasons={absentReasons}
                public
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={timeplanUnitStyles.timeplanDay}>
      {groupedTimeplanUnits.valueSeq().map((tpus) => {
        const customerId = tpus.first().get('customer');
        return (
          <div key={customerId}>
            <div>
              <span className={`${styles.customerName}`}>
                <CustomerName noLink customerId={customerId} />
              </span>
              <TotalTime timeplanUnits={tpus} />
            </div>
            {tpus.map(timeplanUnit => (
              <TimeplanUnit
                key={timeplanUnit.id}
                isEditable={isEditable}
                disabled={disabled}
                resources={resources}
                timeplanUnit={timeplanUnit}
                date={date}
                overlaps={!!overlaps[timeplanUnit.id]}
                duplicates={!!duplicates[timeplanUnit.id]}
                workTypes={workTypes}
                absentReasons={absentReasons}
                invalid={false}
                onChangeTimeplanUnits={onChangeTimeplanUnits}
              />
            ))}
          </div>
        );
      })}
      {!disabled && (
        <div>
          <AddTpu
            date={date}
            timeplanUnits={timeplanUnits}
            onChangeTimeplanUnits={onChangeTimeplanUnits}
            resources={resources}
            workTypes={workTypes}
            allTimeplanUnits={allTimeplanUnits}
          />
        </div>
      )}
      {/*
        // TODO, add warning for booked times etc if relevant
        {isEditable && !disabled && (
          <div>
            {bookedTimeplanUnits.size > 0 && (
              <div className="text-danger">
                <b>Redan bokade tider</b>
                {bookedTimeplanUnits.map(({ id, from, to, resourceId }) => (
                  <div key={id}>
                    {`${from}-${to}`} (<Resource resourceId={resourceId} />)
                  </div>
                ))}
              </div>
            )}
            {resourceReportedTimeplanUnits.size > 0 && (
              <div className="text-danger">Tidrapporterad på denna bokning</div>
            )}
            {reportedTimeplanUnits.size > 0 && (
              <div className="text-danger">Attesterad på denna bokning</div>
            )}
          </div>
        )}
      */}
    </div>
  );
}

TimeplanDay.defaultProps = {
  bookedTimeplanUnits: new List(),
  resourceReportedTimeplanUnits: new List(),
  reportedTimeplanUnits: new List(),
};

TimeplanDay.propTypes = {
  disabled: PropTypes.bool.isRequired,
  date: PropTypes.string.isRequired,
  onChangeTimeplanUnits: PropTypes.func.isRequired,
  resources: PropTypes.instanceOf(List).isRequired,
  groupedTimeplanUnits: PropTypes.instanceOf(Map).isRequired,
  groupedCompareTimeplanUnits: PropTypes.instanceOf(Map).isRequired,
  timeplanUnits: PropTypes.instanceOf(List).isRequired,
  workTypes: PropTypes.instanceOf(List).isRequired,
  absentReasons: PropTypes.instanceOf(List).isRequired,
  isEditable: PropTypes.bool.isRequired,
  bookedTimeplanUnits: PropTypes.instanceOf(List),
  resourceReportedTimeplanUnits: PropTypes.instanceOf(List),
  reportedTimeplanUnits: PropTypes.instanceOf(List),
  allTimeplanUnits: PropTypes.instanceOf(List).isRequired,
};

export default compose(
  // TODO, sort them by customer name alphabetically
  withProps(({ timeplanUnits, compareTimeplanUnits, date }) => ({
    groupedTimeplanUnits: timeplanUnits.reduce((acc, tpu) => {
      const customerId = tpu.get('customer');
      const nextTPUs = acc.has(customerId) ? acc.get(customerId).push(tpu) : new List([tpu]);
      return acc.set(customerId, nextTPUs);
    }, new Map()),
    groupedCompareTimeplanUnits: compareTimeplanUnits.reduce((acc, tpu) => {
      const customerId = tpu.get('customer');
      const nextTPUs = acc.has(customerId) ? acc.get(customerId).push(tpu) : new List([tpu]);
      return acc.set(customerId, nextTPUs);
    }, new Map()),
    date: getDate(date),
  })),
  mapProps(({ allTimeplanUnits, groupedTimeplanUnits, ...props }) => ({
    ...props,
    groupedTimeplanUnits,
    allTimeplanUnits: groupedTimeplanUnits.isEmpty() ? allTimeplanUnits : new List(),
  })),
)(TimeplanDay);
